import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, CalendarClock, FolderKanban, Plus, Pencil, Trash2, Clock, ChevronDown, ChevronRight, X, CheckCircle2, RotateCcw, Archive } from "lucide-react";
import api from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const EMPTY_APP = { data: "", ora: "", titolo: "", note: "", pratica_id: "", numero_pratica: "" };

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

export function toTs(data, ora) {
  if (!data) return Infinity;
  const s = String(data).trim();
  let y, m, d, mm;
  if ((mm = s.match(/^(\d{4})-(\d{2})-(\d{2})/))) { y = +mm[1]; m = +mm[2]; d = +mm[3]; }
  else if ((mm = s.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})/))) { d = +mm[1]; m = +mm[2]; y = +mm[3]; }
  else return Infinity;
  let hh = 0, mi = 0;
  const om = String(ora || "").match(/^(\d{1,2}):(\d{2})/);
  if (om) { hh = +om[1]; mi = +om[2]; }
  return new Date(y, m - 1, d, hh, mi).getTime();
}

export function AppuntamentoDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY_APP);
  const [saving, setSaving] = useState(false);
  const [praticaQuery, setPraticaQuery] = useState("");
  const [praticaResults, setPraticaResults] = useState([]);
  const [praticaOpen, setPraticaOpen] = useState(false);
  useEffect(() => {
    if (open) {
      const init = { ...EMPTY_APP, ...(initial || {}) };
      setF(init);
      setPraticaQuery(init.numero_pratica || "");
      setPraticaResults([]);
      setPraticaOpen(false);
    }
  }, [open, initial]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const searchPratica = async (q) => {
    setPraticaQuery(q);
    if (!q || q.length < 2) { setPraticaResults([]); setPraticaOpen(false); return; }
    try {
      const { data } = await api.get(`/studio/pratiche-search?q=${encodeURIComponent(q)}`);
      setPraticaResults(data || []);
      setPraticaOpen(true);
    } catch { setPraticaResults([]); }
  };
  const pickPratica = (r) => {
    setF((s) => ({ ...s, pratica_id: r.id, numero_pratica: r.numero_pratica }));
    setPraticaQuery(`${r.numero_pratica}${r.nome_assistito ? " · " + r.nome_assistito : ""}`);
    setPraticaOpen(false);
  };
  const clearPratica = () => {
    setF((s) => ({ ...s, pratica_id: "", numero_pratica: "" }));
    setPraticaQuery("");
    setPraticaResults([]);
    setPraticaOpen(false);
  };
  const salva = async () => {
    if (!f.titolo.trim()) { toast.error("Inserisci un titolo per l'appuntamento"); return; }
    if (!f.data) { toast.error("Inserisci la data dell'appuntamento"); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.patch(`/studio/appuntamenti/${initial.id}`, f);
      else await api.post("/studio/appuntamenti", f);
      toast.success(initial?.id ? "Appuntamento aggiornato" : "Appuntamento aggiunto");
      onSaved();
      onOpenChange(false);
    } catch {
      toast.error("Errore nel salvataggio dell'appuntamento");
    } finally { setSaving(false); }
  };
  const inp = "h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm px-2 focus:outline-none focus:ring-1 focus:ring-sky-500/40";
  const lbl = "block text-[11px] uppercase tracking-wider text-slate-400 font-mono mb-1";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="appuntamento-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{initial?.id ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 py-1">
          <label className="text-sm"><span className={lbl}>Data</span>
            <Input type="date" value={f.data} onChange={(e) => set("data", e.target.value)} data-testid="appuntamento-data" className={inp} /></label>
          <label className="text-sm"><span className={lbl}>Ora</span>
            <input type="time" value={f.ora} onChange={(e) => set("ora", e.target.value)} data-testid="appuntamento-ora" className={inp} /></label>
          <label className="text-sm sm:col-span-2"><span className={lbl}>Titolo</span>
            <Input value={f.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="appuntamento-titolo" className="h-9 w-full" /></label>
          <div className="text-sm sm:col-span-2">
            <span className={lbl}>Collega a pratica <span className="text-slate-500 normal-case tracking-normal">(opzionale)</span></span>
            <div className="relative">
              <input value={praticaQuery} onChange={(e) => searchPratica(e.target.value)} onFocus={() => praticaResults.length && setPraticaOpen(true)}
                data-testid="appuntamento-pratica-search" placeholder="Cerca per n° pratica o nome…" autoComplete="off" className={`${inp} pr-16`} />
              <FolderKanban className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              {f.pratica_id && (
                <button type="button" onClick={clearPratica} data-testid="appuntamento-pratica-clear"
                  className="absolute right-8 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded text-slate-400 hover:text-red-400 transition-colors" title="Rimuovi collegamento">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              {praticaOpen && praticaResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-lg max-h-52 overflow-y-auto" data-testid="appuntamento-pratica-results">
                  {praticaResults.map((r) => (
                    <button key={r.id} type="button" onClick={() => pickPratica(r)} data-testid={`appuntamento-pratica-opt-${r.id}`}
                      className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60">
                      <span className="font-mono text-amber-400">{r.numero_pratica}</span>{r.nome_assistito ? ` · ${r.nome_assistito}` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <label className="text-sm sm:col-span-2"><span className={lbl}>Note</span>
            <Textarea value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="appuntamento-note" rows={3} className="w-full bg-slate-900/60 border-slate-700" /></label>
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="appuntamento-cancel" className="text-sm rounded-md border border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700 px-3 py-1.5 transition-colors">Annulla</button>
          <button type="button" onClick={salva} disabled={saving} data-testid="appuntamento-save" className="text-sm rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 transition-colors disabled:opacity-50">{initial?.id ? "Aggiorna" : "Salva"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AgendaPanel({ embedded = false }) {
  const navigate = useNavigate();
  const [promemoria, setPromemoria] = useState([]);
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [archiviati, setArchiviati] = useState([]);
  const [showArchivio, setShowArchivio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { } for new, {id,...} for edit
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pgp_agenda_collapsed") === "1");
  useEffect(() => { localStorage.setItem("pgp_agenda_collapsed", collapsed ? "1" : "0"); }, [collapsed]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/agenda");
      setPromemoria(data.promemoria || []);
      setAppuntamenti(data.appuntamenti || []);
      setArchiviati(data.archiviati || []);
    } catch { toast.error("Errore nel caricamento dell'agenda"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const completa = async (tipo, id) => {
    try { await api.post("/studio/agenda/completa", { tipo, id }); toast.success("Attività completata"); load(); }
    catch { toast.error("Errore nel completamento dell'attività"); }
  };
  const ripristina = async (tipo, id) => {
    try { await api.post("/studio/agenda/ripristina", { tipo, id }); toast.success("Attività ripristinata"); load(); }
    catch { toast.error("Errore nel ripristino dell'attività"); }
  };

  const delArchiviata = async (it) => {
    const msg = it.tipo === "promemoria"
      ? "Eliminare questo promemoria dall'archivio? La nota resterà nel Diario della pratica."
      : "Eliminare definitivamente questo appuntamento dall'archivio?";
    if (!window.confirm(msg)) return;
    try { await api.post("/studio/agenda/elimina-archiviata", { tipo: it.tipo, id: it.id }); toast.success("Attività eliminata dall'archivio"); load(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const delApp = async (id) => {
    if (!window.confirm("Eliminare questo appuntamento?")) return;
    try { await api.delete(`/studio/appuntamenti/${id}`); toast.success("Appuntamento eliminato"); load(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const items = [
    ...promemoria.map((p) => ({ ...p, _type: "promemoria", _ts: toTs(p.data) })),
    ...appuntamenti.map((a) => ({ ...a, _type: "appuntamento", _ts: toTs(a.data, a.ora) })),
  ].sort((a, b) => a._ts - b._ts);

  return (
    <div data-testid="agenda-panel">
      <div className="flex items-center justify-between mb-4">
        <button type="button" onClick={() => setCollapsed((c) => !c)} data-testid="agenda-toggle"
          className="flex items-center gap-2 text-slate-200 hover:text-white transition-colors">
          {collapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          <CalendarDays className="h-5 w-5 text-sky-400" />
          <span className="font-head font-semibold">Agenda</span>
          <span className="text-xs text-slate-500">· {items.length} voci</span>
        </button>
        <button type="button" onClick={() => setDialog({})} data-testid="add-appuntamento-btn"
          className="flex items-center gap-1.5 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white font-semibold px-3 py-1.5 transition-colors">
          <Plus className="h-4 w-4" /> Nuovo Promemoria
        </button>
      </div>

      {!collapsed && (loading ? (
        <div className="pgp-card p-6 text-sm text-slate-400" data-testid="agenda-loading">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="pgp-card p-8 text-center text-sm text-slate-500" data-testid="agenda-empty">
          Nessun promemoria o appuntamento. Aggiungi un appuntamento o crea promemoria nelle pratiche.
        </div>
      ) : (
        <div className="space-y-2" data-testid="agenda-list">
          {items.map((it) => it._type === "promemoria" ? (
            <div key={`p-${it.id}`} data-testid={`agenda-promemoria-${it.id}`}
              className="flex items-start gap-3 pgp-card p-3.5 border-l-4 border-l-amber-500">
              <button type="button" onClick={() => it.pratica_id && navigate(`/app/pratiche?p=${it.pratica_id}`)}
                data-testid={`agenda-promemoria-open-${it.id}`}
                className="flex items-start gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
                <FolderKanban className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-mono text-amber-400/90 bg-amber-500/10 rounded px-1.5 py-0.5">Promemoria pratica</span>
                    {it.numero_pratica && <span className="text-xs font-mono text-slate-300" data-testid={`agenda-promemoria-pratica-${it.id}`}>{it.numero_pratica}{it.cliente ? <span className="font-sans"> - {it.cliente}</span> : null}</span>}
                  </div>
                  <div className="text-sm text-slate-100 mt-1 truncate">{it.descrizione || "—"}</div>
                  {it.soggetto && <div className="text-xs text-slate-500 mt-0.5 truncate">{it.soggetto}</div>}
                </div>
              </button>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <div className="flex items-center gap-1 text-sm text-amber-300 font-mono">
                  <CalendarClock className="h-3.5 w-3.5" /> {formatDate(it.data)}
                </div>
                <button type="button" onClick={() => completa("promemoria", it.id)} data-testid={`complete-promemoria-${it.id}`}
                  className="flex items-center gap-1 text-xs rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-2 py-1 transition-colors">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Completata
                </button>
              </div>
            </div>
          ) : (
            <div key={`a-${it.id}`} data-testid={`agenda-appuntamento-${it.id}`}
              className="flex items-start gap-3 pgp-card p-3.5 border-l-4 border-l-sky-500">
              <CalendarClock className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-mono text-sky-400/90 bg-sky-500/10 rounded px-1.5 py-0.5">Appuntamento</span>
                </div>
                <div className="text-sm text-slate-100 mt-1">{it.titolo || "—"}</div>
                {it.pratica_id && (
                  <button type="button" onClick={() => navigate(`/app/pratiche?p=${it.pratica_id}`)}
                    data-testid={`agenda-appuntamento-pratica-${it.id}`}
                    title={[
                      it.cliente ? `Cliente: ${it.cliente}` : null,
                      it.tipo_pratica ? `Tipo: ${it.tipo_pratica}` : null,
                      it.data_sinistro ? `Data sinistro: ${formatDate(it.data_sinistro)}` : null,
                    ].filter(Boolean).join("\n") || "Pratica collegata"}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded px-2 py-0.5 transition-colors">
                    <FolderKanban className="h-3 w-3" /> {it.numero_pratica ? `Pratica ${it.numero_pratica}` : "Pratica collegata"}{it.cliente ? <span className="font-sans" data-testid={`agenda-appuntamento-cliente-${it.id}`}> - {it.cliente}</span> : null}
                  </button>
                )}
                {it.note && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{it.note}</div>}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <div className="flex items-center gap-1 text-sm text-sky-300 font-mono">
                  <CalendarClock className="h-3.5 w-3.5" /> {formatDate(it.data)}
                  {it.ora && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.ora}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => completa("appuntamento", it.id)} data-testid={`complete-appuntamento-${it.id}`}
                    className="flex items-center gap-1 text-xs rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-2 py-1 transition-colors">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Completata
                  </button>
                  <button type="button" onClick={() => setDialog(it)} data-testid={`edit-appuntamento-${it.id}`} title="Modifica"
                    className="h-7 w-7 flex items-center justify-center rounded-md border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => delApp(it.id)} data-testid={`del-appuntamento-${it.id}`} title="Elimina"
                    className="h-7 w-7 flex items-center justify-center rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {archiviati.length > 0 && (
        <div className="mt-4 border-t border-slate-800 pt-4" data-testid="agenda-archivio">
          <button type="button" onClick={() => setShowArchivio((s) => !s)} data-testid="agenda-archivio-toggle"
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            {showArchivio ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            <Archive className="h-4 w-4 text-slate-400" />
            <span className="font-head font-semibold text-sm">Archivio attività</span>
            <span className="text-xs text-slate-500">· {archiviati.length}</span>
          </button>
          {showArchivio && (
            <div className="space-y-2 mt-3" data-testid="agenda-archivio-list">
              {archiviati.map((it) => (
                <div key={`arch-${it.tipo}-${it.id}`} data-testid={`agenda-archiviata-${it.id}`}
                  className="flex items-start gap-3 pgp-card p-3 border-l-4 border-l-slate-600 opacity-80">
                  {it.tipo === "promemoria" ? <FolderKanban className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> : <CalendarClock className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />}
                  <button type="button" onClick={() => it.pratica_id && navigate(`/app/pratiche?p=${it.pratica_id}`)}
                    data-testid={`agenda-archiviata-open-${it.id}`}
                    className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-mono text-slate-400 bg-slate-700/40 rounded px-1.5 py-0.5">{it.tipo === "promemoria" ? "Promemoria" : "Appuntamento"}</span>
                      {it.numero_pratica && <span className="text-xs font-mono text-slate-400">{it.numero_pratica}{it.cliente ? <span className="font-sans"> - {it.cliente}</span> : null}</span>}
                    </div>
                    <div className="text-sm text-slate-300 mt-1 truncate line-through decoration-slate-600">{it.tipo === "promemoria" ? (it.descrizione || "—") : (it.titolo || "—")}</div>
                    <div className="text-[11px] text-emerald-400/80 mt-1 font-mono flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Completata il {fmtDateTime(it.completata_at)}</div>
                  </button>
                  <button type="button" onClick={() => ripristina(it.tipo, it.id)} data-testid={`restore-${it.tipo}-${it.id}`} title="Ripristina"
                    className="flex items-center gap-1 text-xs rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700/50 px-2 py-1 transition-colors shrink-0">
                    <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                  </button>
                  <button type="button" onClick={() => delArchiviata(it)} data-testid={`del-archiviata-${it.tipo}-${it.id}`} title="Elimina dall'archivio"
                    className="h-7 w-7 flex items-center justify-center rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AppuntamentoDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} initial={dialog} onSaved={load} />
    </div>
  );
}

export default function Agenda() {
  return (
    <div data-testid="agenda-page">
      <PageHeader title="Agenda" subtitle="Promemoria delle pratiche in gestione e appuntamenti dello studio." icon={CalendarDays} />
      <AgendaPanel />
    </div>
  );
}
